import { PreloadConfig } from './config-types';
import { logger } from './logger';
import { isResolvedUri, toAbsoluteURI } from './util';

export class ResourceLoader {
    /**
     * In-memory cache for resolved URLs. This is used to avoid redundant network requests for
     * the same asset URI. The cache is a Map where the key is the asset URI and the value is
     * the resolved URL.
     */
    private resolvedUriCache: Map<string, string> = new Map();

    /**
     * Resolves the full URL for a given asset. Non-file URIs are treated as paths and resolved
     * using the `toAbsoluteURI` method. If the assetUri is already resolved, or if it's found in
     * cache, it will be returned directly. If an assetUrlResolver function is provided in the
     * configuration, it will be used to resolve the URL if the assetUri is not fully qualified,
     * a blob URL, or in the cache.
     *
     * @param config - The preload configuration.
     * @param assetUri - The asset URI from the config.
     * @returns The resolved full URL.
     */
    public async resolveUri(config: PreloadConfig, assetUri?: string): Promise<string> {
        if (!assetUri) {
            logger.errorAndThrow('Asset URI is undefined.');
        }

        // Check if the URL is already cached
        if (this.resolvedUriCache.has(assetUri)) {
            return this.resolvedUriCache.get(assetUri) as string;
        }

        let resolvedUrl: string | undefined;

        if (isResolvedUri(assetUri)) {
            // If the assetUri is fully qualified or a blob URL, return it directly.
            // (Assets uris are resolved before being sent to the worker.)
            resolvedUrl = assetUri;
        } else if (!assetUri.match(/\.[a-z0-9]+$/i)) {
            // Resolve as an absolute path to a non-file endpoint (e.g., wasmPaths).
            resolvedUrl = toAbsoluteURI(assetUri, config.baseUrl);
        } else if (typeof config.assetUrlResolver === 'function') {
            // Resolve via assetUrlResolver. This delegates the resolution logic to the caller.
            resolvedUrl = await config.assetUrlResolver(assetUri, config);
        }

        if (!resolvedUrl) {
            logger.errorAndThrow(`Failed to resolve asset URI: ${assetUri}`);
        }

        // Cache the resolved URL
        this.resolvedUriCache.set(assetUri, resolvedUrl);

        return resolvedUrl;
    }
}

// The resource loader is a singleton that can be used to load resources
export const resourceLoader = new ResourceLoader();
